import React, { useState, useRef, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Activity, Play, Square, Download, Trash2, Loader } from 'lucide-react'

/* PingTool dùng WebSocket (/ws) cho real-time streaming.
   – Trước đây dùng REST /api/ping, count*interval cao sẽ vượt nginx proxy_read_timeout 300s
   – WS không có giới hạn này, và mỗi kết quả hiện ngay (không phải đợi xong rồi mới hiện)
   – Backend giới hạn 1 WS / IP — mở tab thứ 2 sẽ đóng tab cũ */
export default function PingTool({ wanIp }) {
  const [host, setHost]       = useState('')
  const [count, setCount]     = useState(10)
  const [intervalMs, setIntervalMs] = useState(1000)
  const [running, setRunning] = useState(false)
  const [logs, setLogs]       = useState([])
  const [summary, setSummary] = useState(null)
  const [progress, setProgress] = useState(0)
  const logRef    = useRef(null)
  const wsRef     = useRef(null)
  const expectedCountRef = useRef(0)

  useEffect(() => {
    if (wanIp && !host) setHost(wanIp)
  }, [wanIp])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [logs])

  /* Đóng WS khi component unmount để không leak connection phía server */
  useEffect(() => () => { try { wsRef.current?.close() } catch (_) {} }, [])

  const finalize = (toastOk = true) => {
    setRunning(false)
    /* Dùng functional setLogs để đọc state mới nhất rồi compute summary */
    setLogs(prev => {
      const latencies = prev.filter(l => l.latency != null).map(l => l.latency)
      const sent      = prev.length
      const received  = latencies.length
      const lost      = sent - received
      const s = {
        sent, received, lost,
        lossPercent: sent ? ((lost / sent) * 100).toFixed(1) : '0.0',
        minLatency:  latencies.length ? Math.min(...latencies) : null,
        maxLatency:  latencies.length ? Math.max(...latencies) : null,
        avgLatency:  latencies.length ? Math.round(latencies.reduce((a,b)=>a+b,0) / latencies.length) : null,
      }
      setSummary(s)
      if (toastOk) {
        if (lost > 0) toast.error(`Cảnh báo: ${s.lossPercent}% gói tin bị mất!`)
        else if (sent > 0) toast.success('Ping hoàn tất – không mất gói!')
      }
      return prev
    })
  }

  const startPing = () => {
    const h = host.trim()
    if (!h) return toast.error('Vui lòng nhập Host/IP hoặc tên miền')
    const safeCount = Math.max(1, Math.min(100, parseInt(count) || 10))
    expectedCountRef.current = safeCount

    setRunning(true)
    setLogs([])
    setSummary(null)
    setProgress(0)

    /* Đóng WS cũ (nếu có) trước khi mở mới */
    try { wsRef.current?.close() } catch (_) {}

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${proto}//${window.location.host}/ws`
    let ws
    try { ws = new WebSocket(wsUrl) }
    catch (e) {
      toast.error('Không mở được WebSocket: ' + e.message)
      setRunning(false)
      return
    }
    wsRef.current = ws

    ws.onopen = () => {
      const duration = Math.max(1, Math.ceil((safeCount * intervalMs) / 1000))
      ws.send(JSON.stringify({
        type:     'start_ping',
        host:     h,
        interval: intervalMs,
        duration,
      }))
    }

    ws.onmessage = (ev) => {
      let d
      try { d = JSON.parse(ev.data) } catch { return }

      if (d.type === 'ping_result') {
        const entry = {
          seq:     d.seq,
          time:    d.timestamp,
          ts:      new Date(d.timestamp).toLocaleTimeString('vi-VN'),
          status:  d.lost ? 'timeout' : 'success',
          latency: d.lost ? null : d.latency,
        }
        setLogs(prev => [...prev, entry])
        setProgress(Math.round((d.seq / expectedCountRef.current) * 100))
      } else if (d.type === 'ping_done' || d.type === 'ping_stopped') {
        finalize(d.type === 'ping_done')
        try { ws.close() } catch (_) {}
      }
    }

    ws.onerror = () => {
      toast.error('Lỗi kết nối WebSocket')
      setRunning(false)
    }

    ws.onclose = (ev) => {
      if (running) finalize(false)
      setRunning(false)
      /* code 1008 = backend đóng do "Replaced by new connection from same IP" */
      if (ev.code === 1008 && /Replaced/i.test(ev.reason || '')) {
        toast('Đã bị thay thế bởi phiên ping khác từ cùng IP', { icon: '⚠️' })
      }
    }
  }

  const stopPing = () => {
    try { wsRef.current?.send(JSON.stringify({ type: 'stop_ping' })) } catch (_) {}
    /* WS sẽ trả ping_stopped → finalize qua onmessage. Phòng trường hợp lỗi, close sau 1.5s */
    setTimeout(() => { try { wsRef.current?.close() } catch (_) {} }, 1500)
    toast('Đang dừng ping...', { icon: '⏹' })
  }

  const exportLog = () => {
    const lines = [
      `=== TranAnhWebTool – Ping Log ===`,
      `Host: ${host}`,
      `Thời gian: ${new Date().toLocaleString('vi-VN')}`,
      '',
      ...logs.map(l =>
        l.status === 'success'
          ? `[${l.ts}] seq=${l.seq}  ✓ Thành công  latency=${l.latency}ms`
          : `[${l.ts}] seq=${l.seq}  ✗ THẤT BẠI (timeout)`
      ),
      '',
      summary ? [
        '--- Tổng kết ---',
        `Gửi: ${summary.sent}  Nhận: ${summary.received}  Mất: ${summary.lost} (${summary.lossPercent}%)`,
        `Latency: min=${summary.minLatency}ms  avg=${summary.avgLatency}ms  max=${summary.maxLatency}ms`,
      ].join('\n') : '',
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `ping-${host}-${Date.now()}.txt`; a.click()
    URL.revokeObjectURL(url)
  }

  const getLossPeriods = () => {
    const periods = []; let lossStart = null
    logs.forEach((l, i) => {
      const failed = l.status !== 'success'
      if (failed && lossStart === null) lossStart = i
      if (!failed && lossStart !== null) {
        periods.push({ from: logs[lossStart].ts, to: logs[i - 1].ts, count: i - lossStart })
        lossStart = null
      }
    })
    if (lossStart !== null) {
      periods.push({ from: logs[lossStart].ts, to: logs[logs.length - 1].ts, count: logs.length - lossStart })
    }
    return periods
  }

  const successLogs = logs.filter(l => l.latency)
  const maxLatency = successLogs.length ? Math.max(...successLogs.map(l => l.latency)) : 1

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Title */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center glow-green"
          style={{ background: 'linear-gradient(135deg, var(--accent3), #0d9488)' }}>
          <Activity size={20} className="text-black" />
        </div>
        <div>
          <h1 className="font-display text-lg">Ping & Monitor</h1>
          <p className="text-xs text-[var(--text3)]">Kiểm tra kết nối TCP tới host, ghi log và phát hiện mất gói</p>
        </div>
      </div>

      {/* Config */}
      <div className="grad-border p-5 space-y-4">
        <div>
          <label className="text-xs text-[var(--text3)] mb-1.5 block">IP / Tên miền đích</label>
          <input
            className="inp"
            placeholder="google.com  hoặc  8.8.8.8  hoặc  192.168.1.1"
            value={host}
            onChange={e => setHost(e.target.value)}
            disabled={running}
            onKeyDown={e => e.key === 'Enter' && !running && startPing()}
          />
          <div className="text-xs text-[var(--text3)] mt-1">
            💡 Ping kiểm tra bằng cách kết nối TCP port 80 (HTTP) hoặc 443 (HTTPS)
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[var(--text3)] mb-1.5 block">Số lần ping (tối đa 100)</label>
            <input
              className="inp"
              type="number" min="1" max="100"
              value={count}
              onChange={e => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              disabled={running}
            />
          </div>
          <div>
            <label className="text-xs text-[var(--text3)] mb-1.5 block">Khoảng cách</label>
            <select className="inp" value={intervalMs}
              onChange={e => setIntervalMs(parseInt(e.target.value))} disabled={running}>
              <option value={500}>0.5 giây</option>
              <option value={1000}>1 giây</option>
              <option value={2000}>2 giây</option>
              <option value={5000}>5 giây</option>
            </select>
          </div>
        </div>
        <div className="text-xs text-[var(--text3)]">
          ⏱ Timeout mỗi ping cố định ở 2 giây (TCP connect tới port 80/443)
        </div>

        <div className="flex gap-3 flex-wrap">
          {!running ? (
            <button className="btn btn-success" onClick={startPing}>
              <Play size={14} /> Bắt đầu Ping
            </button>
          ) : (
            <button className="btn btn-danger" onClick={stopPing}>
              <Square size={14} /> Dừng
            </button>
          )}
          {logs.length > 0 && !running && (
            <>
              <button className="btn btn-ghost" onClick={exportLog}>
                <Download size={14} /> Xuất log (.txt)
              </button>
              <button className="btn btn-ghost" onClick={() => { setLogs([]); setSummary(null) }}>
                <Trash2 size={14} /> Xóa log
              </button>
            </>
          )}
        </div>

        {running && (
          <div>
            <div className="flex justify-between text-xs text-[var(--text3)] mb-2">
              <span className="flex items-center gap-2">
                <Loader size={12} className="animate-spin" />
                Đang ping <span className="text-[var(--accent)] font-mono">{host}</span>...
              </span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${progress}%`,
                background: 'linear-gradient(to right, var(--accent2), var(--accent3))',
                transition: 'width .3s'
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="fade-in grad-border p-5">
          <h3 className="font-display text-base mb-4">📊 Tổng kết — <span className="text-[var(--accent)] font-mono">{host}</span></h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Đã gửi',     val: summary.sent,     color: 'var(--text)' },
              { label: 'Nhận được',  val: summary.received,  color: 'var(--success)' },
              { label: 'Mất gói',    val: `${summary.lost} (${summary.lossPercent}%)`, color: summary.lost > 0 ? 'var(--danger)' : 'var(--success)' },
              { label: 'Latency TB', val: summary.avgLatency ? `${summary.avgLatency}ms` : 'N/A', color: 'var(--accent)' },
            ].map(item => (
              <div key={item.label} className="p-3 rounded-xl border text-center"
                style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                <div className="text-xs text-[var(--text3)] mb-1">{item.label}</div>
                <div className="font-mono font-bold text-lg" style={{ color: item.color }}>{item.val}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-lg border text-center" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              <div className="text-xs text-[var(--text3)] mb-1">Min</div>
              <div className="font-mono font-bold text-[var(--success)]">{summary.minLatency !== null ? `${summary.minLatency}ms` : 'N/A'}</div>
            </div>
            <div className="p-3 rounded-lg border text-center" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              <div className="text-xs text-[var(--text3)] mb-1">Max</div>
              <div className="font-mono font-bold text-[var(--warn)]">{summary.maxLatency !== null ? `${summary.maxLatency}ms` : 'N/A'}</div>
            </div>
          </div>
          {(() => {
            const periods = getLossPeriods()
            return periods.length > 0 ? (
              <div className="p-3 rounded-lg" style={{ background: 'rgba(248,113,113,.07)', border: '1px solid rgba(248,113,113,.2)' }}>
                <div className="text-sm font-semibold text-[var(--danger)] mb-2">⚠️ Các khoảng mất gói:</div>
                {periods.map((p, i) => (
                  <div key={i} className="text-sm font-mono text-[var(--text2)]">
                    → {p.from} → {p.to} <span className="text-[var(--danger)]">({p.count} gói)</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 rounded-lg text-sm text-[var(--success)]"
                style={{ background: 'rgba(52,211,153,.07)', border: '1px solid rgba(52,211,153,.2)' }}>
                ✅ Không có gói tin nào bị mất trong phiên này
              </div>
            )
          })()}
        </div>
      )}

      {/* Log */}
      {logs.length > 0 && (
        <div className="fade-in grad-border p-5">
          <h3 className="font-display text-base mb-3">📋 Log chi tiết</h3>
          {/* Mini chart */}
          <div className="mb-4">
            <div className="text-xs text-[var(--text3)] mb-2">Biểu đồ latency (đỏ = mất gói)</div>
            <div className="flex items-end gap-px rounded-lg p-2" style={{ height: 72, background: 'var(--bg)' }}>
              {logs.map((l, i) => {
                const barH = l.status === 'success'
                  ? Math.max(4, (l.latency / maxLatency) * 52)
                  : 52
                const color = l.status === 'success'
                  ? `rgba(52,211,153,${0.35 + (l.latency / maxLatency) * 0.65})`
                  : 'var(--danger)'
                return (
                  <div key={i} className="flex-1 rounded-t transition-all cursor-pointer"
                    style={{ height: barH, background: color, minWidth: 2 }}
                    title={`#${l.seq} [${l.ts}]: ${l.status === 'success' ? l.latency + 'ms' : 'timeout'}`}
                  />
                )
              })}
            </div>
          </div>
          <div className="log-output" ref={logRef}>
            {logs.map((l, i) => (
              <div key={i} className={l.status === 'success' ? 'log-success' : 'log-error'}>
                [{l.ts}] seq={String(l.seq).padStart(3, ' ')}  {' '}
                {l.status === 'success'
                  ? `✓  latency: ${l.latency}ms`
                  : '✗  TIMEOUT – không phản hồi'
                }
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
